// =============================================================================
// Character Stat Resolver
//
// Maps human-readable stat keys to live values on the Character record.
// Used by the roll engine when an attack or damage modifier is specified
// as a stat reference rather than a hardcoded number.
// =============================================================================
import type { Character, RuleSystem } from '../../../backend/shared/types/index';

export interface StatDefinition {
  key:        string;   // stored key, e.g. 'bab', 'str_mod'
  label:      string;   // UI label, e.g. 'Base Attack Bonus'
  systems:    RuleSystem[] | 'all';
  resolve:    (char: Character) => number;
}

/** Ability score modifier: floor((score - 10) / 2) */
function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

const SIZE_ATTACK_MODIFIER: Record<NonNullable<Character['size']>, number> = {
  Fine: 8,
  Diminutive: 4,
  Tiny: 2,
  Small: 1,
  Medium: 0,
  Large: -1,
  Huge: -2,
  Gargantuan: -4,
  Colossal: -8,
};

export const STAT_DEFINITIONS: readonly StatDefinition[] = [
  // ── Attack progressions (D&D 3.5 / PF1 iterative attack sequence) ────────
  // Iterative attacks by BAB:
  //   BAB  +0 to  +5: 1 attack  (BAB)
  //   BAB  +6 to +10: 2 attacks (BAB, BAB−5)
  //   BAB +11 to +15: 3 attacks (BAB, BAB−5, BAB−10)
  //   BAB +16 to +20: 4 attacks (BAB, BAB−5, BAB−10, BAB−15)
  {
    key: 'bab', label: 'BAB Max',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => c.baseAttackBonus ?? 0,
  },
  {
    key: 'bab_2nd', label: 'BAB 2nd Iterative',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => (c.baseAttackBonus ?? 0) - 5,
  },
  {
    key: 'bab_3rd', label: 'BAB 3rd Iterative',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => (c.baseAttackBonus ?? 0) - 10,
  },
  {
    key: 'bab_4th', label: 'BAB 4th Iterative',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => (c.baseAttackBonus ?? 0) - 15,
  },
  // ── Ability score modifiers ───────────────────────────────────────────────
  {
    key: 'str_mod', label: 'Strength Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.str ?? 10),
  },
  {
    // Off-hand attacks in D&D 3.5 / PF1 add only ½ STR modifier to damage.
    // Always rounds DOWN (Math.floor) per D&D damage rules.
    // e.g. STR 18 → mod +4 → ×½ = +2;  STR 15 → mod +2 → ×½ = +1
    key: 'str_mod_half', label: 'STR ×½ (off-hand)',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => Math.floor(abilityMod(c.abilityScores?.str ?? 10) * 0.5),
  },
  {
    // Two-handed weapons in D&D 3.5 / PF1 add 1½ × STR modifier to damage.
    // Always rounds DOWN (Math.floor) per D&D damage rules.
    // e.g. STR 16 → mod +3 → ×1½ = +4;  STR 14 → mod +2 → ×1½ = +3
    key: 'str_mod_1_5', label: 'STR ×1½ (two-handed)',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => Math.floor(abilityMod(c.abilityScores?.str ?? 10) * 1.5),
  },
  {
    key: 'dex_mod', label: 'Dexterity Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.dex ?? 10),
  },
  {
    key: 'con_mod', label: 'Constitution Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.con ?? 10),
  },
  {
    key: 'int_mod', label: 'Intelligence Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.int ?? 10),
  },
  {
    key: 'wis_mod', label: 'Wisdom Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.wis ?? 10),
  },
  {
    key: 'cha_mod', label: 'Charisma Modifier',
    systems: 'all',
    resolve: (c) => abilityMod(c.abilityScores?.cha ?? 10),
  },
  // ── Size modifier to attack rolls (creature size, not weapon damage size) ─
  {
    key: 'size_mod', label: 'Size Modifier',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => SIZE_ATTACK_MODIFIER[c.size ?? 'Medium'] ?? 0,
  },
  // ── Combat maneuvers ────────────────────────────────────────────────────
  {
    key: 'cmb', label: 'Combat Maneuver Bonus (CMB)',
    systems: ['pathfinder1'],
    resolve: (c) => c.cmb ?? 0,
  },
  {
    key: 'cmd', label: 'Combat Maneuver Defense (CMD)',
    systems: ['pathfinder1'],
    resolve: (c) => c.cmd ?? 10,
  },
  // ── Other ─────────────────────────────────────────────────────────────────
  {
    key: 'initiative', label: 'Initiative Modifier',
    systems: 'all',
    resolve: (c) => c.initiative ?? 0,
  },
  {
    key: 'spell_dc', label: 'Spell Save DC',
    systems: 'all',
    resolve: (c) => c.spellSaveDC ?? 10,
  },
  {
    key: 'concentration', label: 'Concentration Bonus',
    systems: ['dnd35', 'pathfinder1'],
    resolve: (c) => c.concentrationBonus ?? 0,
  },
  {
    key: 'total_level', label: 'Total Character Level',
    systems: 'all',
    resolve: (c) => c.totalLevel ?? 1,
  },
  // ── D&D 5e ──────────────────────────────────────────────────────────────────
  {
    key: 'proficiency_bonus', label: 'Proficiency Bonus',
    systems: ['dnd5e'],
    // Use explicitly stored value; compute from totalLevel as fallback.
    resolve: (c) => {
      if (c.proficiencyBonus !== undefined) return c.proficiencyBonus;
      const lvl = c.totalLevel ?? 1;
      return lvl <= 4 ? 2 : lvl <= 8 ? 3 : lvl <= 12 ? 4 : lvl <= 16 ? 5 : 6;
    },
  },
  {
    key: 'spell_attack_bonus', label: 'Spell Attack Bonus',
    systems: ['dnd5e'],
    resolve: (c) => c.spellAttackBonus ?? 0,
  },
];

/** Quick lookup: key → definition */
export const STAT_DEF_MAP: Readonly<Record<string, StatDefinition>> =
  Object.fromEntries(STAT_DEFINITIONS.map((s) => [s.key, s]));

/** Stat definitions applicable to a given system. */
export function statsForSystem(system: RuleSystem): StatDefinition[] {
  return STAT_DEFINITIONS.filter(
    (s) => s.systems === 'all' || (s.systems as RuleSystem[]).includes(system),
  );
}

/** Default stat ref for newly-added modifier components in a given system. */
export function defaultStatKeyForSystem(system: RuleSystem): string {
  return statsForSystem(system)[0]?.key ?? 'str_mod';
}

/**
 * Normalize stat refs that are impossible for a routine's system.
 *
 * D&D 5e attack rows previously defaulted new stat refs to the PF/3.5 `bab`
 * key. Because the 5e dropdown did not contain that value, browsers displayed
 * the first visible option, Strength Modifier, while the stored key remained
 * `bab` and resolved to 0 at roll time.
 */
export function normalizeStatKeyForSystem(statKey: string, system?: RuleSystem): string {
  if (!system) return statKey;
  if (statsForSystem(system).some((s) => s.key === statKey)) return statKey;
  if (system === 'dnd5e' && ['bab', 'bab_2nd', 'bab_3rd', 'bab_4th'].includes(statKey)) {
    return 'str_mod';
  }
  return statKey;
}

/**
 * An attack modifier source — either a fixed number or a stat reference key.
 * Stored as `attackModSource` on AttackNode (alongside the existing `attackMod`
 * numeric field which holds the resolved value for display when no char is available).
 */
export type AttackModSource =
  | { type: 'manual';   value: number }
  | { type: 'stat_ref'; key: string };

/**
 * Resolve an AttackModSource against a live character record.
 * Falls back to `manualFallback` when the character is not available.
 */
export function resolveAttackModSource(
  source:          AttackModSource,
  char?:           Character | null,
  manualFallback?: number,
): number {
  if (source.type === 'manual') return source.value;
  if (!char) return manualFallback ?? 0;
  const def = STAT_DEF_MAP[source.key];
  return def ? def.resolve(char) : (manualFallback ?? 0);
}

/**
 * Convenience: parse a stored attackModSource JSON blob from DynamoDB
 * (may be absent on older routines — falls back to the numeric attackMod).
 */
export function parseAttackModSource(
  raw:       unknown,
  fallback:  number,
): AttackModSource {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (r.type === 'manual' && typeof r.value === 'number') {
      return { type: 'manual', value: r.value };
    }
    if (r.type === 'stat_ref' && typeof r.key === 'string') {
      return { type: 'stat_ref', key: r.key };
    }
  }
  return { type: 'manual', value: fallback };
}

import type { ModifierComponent, ResolvedModComponent } from '../../../backend/shared/types/index';

/**
 * Resolve an array of ModifierComponents against an optional live character.
 * Returns the summed total and a per-component label+value list for display.
 *
 * @param components  The modifier component array to resolve.
 * @param char        Optional live character record for stat_ref resolution.
 * @param extraFlat   Additional flat bonus (e.g. from campaign modifier).
 */
export function resolveComponents(
  components: ModifierComponent[],
  char?:      import('../../../backend/shared/types/index').Character | null,
  extraFlat = 0,
  gameSystem?: RuleSystem,
): { total: number; resolved: ResolvedModComponent[] } {
  const resolved: ResolvedModComponent[] = [];
  let total = extraFlat;

  for (const c of components) {
    if (c.type === 'stat_ref' && c.statKey) {
      const statKey = normalizeStatKeyForSystem(c.statKey, gameSystem);
      const def = STAT_DEF_MAP[statKey];
      const val = def && char ? def.resolve(char) : 0;
      resolved.push({ label: def?.label ?? statKey, value: val });
      total += val;
    } else if (c.type === 'manual' && typeof c.value === 'number') {
      const label = c.descriptor?.trim()
        || (c.value >= 0 ? `+${c.value}` : String(c.value));
      resolved.push({ label, value: c.value });
      total += c.value;
    }
  }

  return { total, resolved };
}
